import React from "react";
import axios from "axios";
import { toast } from "sonner";
import { Dialog, DialogContent } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { CreditCard } from "lucide-react";
import { useT } from "@/i18n/lang";
import { buildFinancingUrl } from "@/data/dealership";
import { trackEvent } from "@/lib/tracker";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * A wrapper that intercepts financing button clicks with a quick-capture modal.
 * Children is any clickable trigger element; we override its onClick.
 */
export default function FinancingCTA({ page = "unknown", children, className, testid }) {
  const { t } = useT();
  const [open, setOpen] = React.useState(false);
  const [form, setForm] = React.useState({ name: "", phone: "", email: "", notes: "" });
  const [loading, setLoading] = React.useState(false);

  const openModal = (e) => {
    e?.preventDefault?.();
    setOpen(true);
  };

  const goToFinancing = () => {
    trackEvent("financing_click", page);
    window.open(buildFinancingUrl(), "_blank", "noopener");
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim() || !form.phone.trim()) {
      toast.error(t("fin.fillAll"));
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/inquiries`, {
        name: form.name,
        email: form.email || `${form.phone.replace(/\D/g, "")}@no-email.local`,
        phone: form.phone,
        subject: "🔔 Financing lead (before redirect)",
        message:
          `Customer clicked "Apply for Financing" from the ${page} section.\n\n` +
          `Name: ${form.name}\nPhone: ${form.phone}\nEmail: ${form.email || "(not provided)"}\n\n` +
          `Notes: ${form.notes || "(none)"}\n\n` +
          `They are being redirected to autonationtoyotasouthaustin.com/financing now. Follow up right away.`,
      });
      toast.success(t("fin.success"));
      setForm({ name: "", phone: "", email: "", notes: "" });
      setOpen(false);
      goToFinancing();
    } catch (err) {
      // Even on failure, don't block them — send them through and log a raw track event
      toast.error("Sending you to the application...");
      trackEvent("financing_click_fallback", page);
      setOpen(false);
      window.open(buildFinancingUrl(), "_blank", "noopener");
    } finally {
      setLoading(false);
    }
  };

  const skip = () => {
    trackEvent("financing_click_skipped", page);
    setOpen(false);
    window.open(buildFinancingUrl(), "_blank", "noopener");
  };

  return (
    <>
      {React.cloneElement(children, {
        onClick: openModal,
        "data-testid": testid || children.props["data-testid"],
        className: className || children.props.className,
      })}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent
          data-testid="financing-modal"
          className="rounded-none border border-[#E4E4E7] p-0 max-w-md gap-0 bg-white"
        >
          <div className="bg-[#EB0A1E] h-1.5" />
          <div className="p-6 md:p-8">
            <div className="w-12 h-12 bg-[#0A0A0A] text-white flex items-center justify-center mb-4">
              <CreditCard className="w-5 h-5" />
            </div>
            <h3 className="font-display font-bold text-2xl md:text-3xl text-[#0A0A0A] tracking-tight leading-tight">
              {t("fin.title")}
            </h3>
            <p className="mt-2 text-sm text-[#52525B] leading-relaxed">
              {t("fin.subtitle")}
            </p>

            <form onSubmit={submit} className="mt-6 space-y-4" data-testid="financing-form">
              <div className="space-y-1.5">
                <Label htmlFor="fin-name" className="text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
                  {t("fin.name")}
                </Label>
                <Input
                  id="fin-name"
                  data-testid="fin-name"
                  required
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fin-phone" className="text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
                  {t("fin.phone")}
                </Label>
                <Input
                  id="fin-phone"
                  type="tel"
                  data-testid="fin-phone"
                  required
                  value={form.phone}
                  onChange={(e) => setForm({ ...form, phone: e.target.value })}
                  className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fin-email" className="text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
                  {t("fin.email")}
                </Label>
                <Input
                  id="fin-email"
                  type="email"
                  data-testid="fin-email"
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E]"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="fin-notes" className="text-xs font-bold uppercase tracking-[0.12em] text-[#0A0A0A]">
                  {t("fin.notes")}
                </Label>
                <Textarea
                  id="fin-notes"
                  rows={2}
                  data-testid="fin-notes"
                  placeholder={t("fin.notesPh")}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="rounded-none border-[#E4E4E7] focus-visible:ring-[#EB0A1E] resize-none"
                />
              </div>

              <button
                type="submit"
                disabled={loading}
                data-testid="fin-submit"
                className="w-full bg-[#EB0A1E] hover:bg-[#CC0011] text-white font-semibold py-3.5 transition-colors disabled:opacity-50"
              >
                {loading ? t("fin.submitting") : t("fin.submit")}
              </button>

              <button
                type="button"
                onClick={skip}
                data-testid="fin-skip"
                className="w-full text-xs text-[#52525B] hover:text-[#0A0A0A] underline underline-offset-4"
              >
                {t("fin.skip")}
              </button>
            </form>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
}
